import React, { useState, useEffect } from 'react';
import { sqlAFechaLocal } from '../utils/dateUtils';
import {
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Box,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Avatar,
  Chip,
  FormHelperText,
  Card,
  CardContent,
  Divider,
  OutlinedInput
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  ContactMail as ContactIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Star as StarIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function TrabajadorForm() {
  const [trabajador, setTrabajador] = useState({
    nombre_trabajador: '',
    apellido_trabajador: '',
    tipo_documento: '',
    numero_documento: '',
    fecha_nacimiento: '',
    genero: '',
    direccion_trabajador: '',
    telefono_trabajador: '',
    email_trabajador: '',
    fecha_contratacion: '',
    profesion: '',
    id_escala: '',
    id_nivel_usuario: '',
    locales: [],
    foto: null,
    contacto_emergencia: ''
  });

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  
  const [formData, setFormData] = useState({
    escalas: [],
    nivelesUsuario: [],
    locales: [],
    tiposDocumento: [
      { id: 'DNI', label: 'DNI' },
      { id: 'CE', label: 'Carnet de Extranjería' },
      { id: 'PASAPORTE', label: 'Pasaporte' }
    ],
    generos: [
      { id: 'M', label: 'Masculino' },
      { id: 'F', label: 'Femenino' },
      { id: 'O', label: 'Otro' }
    ]
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isEditing = Boolean(id);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [
        escalasRes,
        nivelesRes,
        localesRes,
        trabajadorRes
      ] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/general/escala`),
        axios.get(`${config.API_BASE_URL}/api/general/nivelusuario`),
        axios.get(`${config.API_BASE_URL}/api/locales`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        id ? axios.get(`${config.API_BASE_URL}/api/trabajadores/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }) : Promise.resolve({ data: null })
      ]);

      setFormData(prev => ({
        ...prev,
        escalas: escalasRes.data,
        nivelesUsuario: nivelesRes.data,
        locales: localesRes.data
      }));

      if (id && trabajadorRes.data) {
        const data = trabajadorRes.data;
        setTrabajador({
          ...data,
          fecha_nacimiento: data.fecha_nacimiento ? sqlAFechaLocal(data.fecha_nacimiento) : '',
          fecha_contratacion: data.fecha_contratacion ? sqlAFechaLocal(data.fecha_contratacion) : '',
          locales: data.locales ? data.locales.filter(l => l !== null) : []
        });
      }

      setError(null);
    } catch (err) {
      setError('Error al cargar los datos necesarios');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!trabajador.nombre_trabajador?.trim()) {
      errors.nombre_trabajador = 'El nombre es requerido';
    }
    
    if (!trabajador.apellido_trabajador?.trim()) {
      errors.apellido_trabajador = 'El apellido es requerido';
    }
    
    if (!trabajador.tipo_documento) {
      errors.tipo_documento = 'El tipo de documento es requerido';
    }
    
    if (!trabajador.numero_documento?.trim()) {
      errors.numero_documento = 'El número de documento es requerido';
    }
    
    if (!trabajador.email_trabajador?.trim()) {
      errors.email_trabajador = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(trabajador.email_trabajador)) {
      errors.email_trabajador = 'El email no es válido';
    }
    
    if (!trabajador.telefono_trabajador?.trim()) {
      errors.telefono_trabajador = 'El teléfono es requerido';
    }
    
    if (!trabajador.fecha_contratacion) {
      errors.fecha_contratacion = 'La fecha de contratación es requerida';
    }
    
    if (!trabajador.id_escala) {
      errors.id_escala = 'La escala es requerida';
    }
    
    if (!trabajador.id_nivel_usuario) {
      errors.id_nivel_usuario = 'El nivel de usuario es requerido';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTrabajador(prev => ({ ...prev, [name]: value }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLocalesChange = (event) => {
    const value = event.target.value;
    setTrabajador(prev => ({
      ...prev,
      locales: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);
    
    try {
      const formattedData = {
        ...trabajador,
        id_empresa: user.id_empresa
      };

      if (id) {
        await axios.put(
          `${config.API_BASE_URL}/api/trabajadores/${id}`, 
          formattedData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } else {
        await axios.post(
          `${config.API_BASE_URL}/api/trabajadores`, 
          formattedData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/home/trabajadores');
      }, 1500);
      
    } catch (err) {
      setError(isEditing ? 'Error al actualizar el trabajador' : 'Error al crear el trabajador');
      console.error('Error:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/home/trabajadores');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getInitials = () => {
    return `${trabajador.nombre_trabajador?.[0] || ''}${trabajador.apellido_trabajador?.[0] || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos del trabajador...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header con botón volver */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{
              color: '#1A237E',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>
          
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              {isEditing ? 'Editar Trabajador' : 'Nuevo Trabajador'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Modifica la información del trabajador' : 'Ingresa los datos del nuevo trabajador'}
            </Typography>
          </Box>
        </Box>

        {/* Preview del trabajador */}
        {(trabajador.nombre_trabajador || trabajador.apellido_trabajador) && (
          <Card sx={{ 
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(79, 195, 247, 0.1)',
            border: '1px solid rgba(79, 195, 247, 0.3)',
            minWidth: 200
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#1A237E', width: 48, height: 48 }}>
                {getInitials()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Vista previa
                </Typography>
              </Box>
            </Box>
          </Card>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Trabajador {isEditing ? 'actualizado' : 'creado'} exitosamente. Redirigiendo...
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          
          {/* Información Personal */}
          <Grid item xs={12}>
            <Paper sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(26, 35, 126, 0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                color: 'white',
                p: { xs: 3, sm: 4 },
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <PersonIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
                  Información Personal
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      name="nombre_trabajador"
                      value={trabajador.nombre_trabajador}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.nombre_trabajador}
                      helperText={fieldErrors.nombre_trabajador}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Apellido"
                      name="apellido_trabajador"
                      value={trabajador.apellido_trabajador}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.apellido_trabajador}
                      helperText={fieldErrors.apellido_trabajador}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required error={!!fieldErrors.tipo_documento}>
                      <InputLabel>Tipo de Documento</InputLabel>
                      <Select
                        name="tipo_documento"
                        value={trabajador.tipo_documento}
                        onChange={handleChange}
                        label="Tipo de Documento"
                        sx={{ borderRadius: 2 }}
                      >
                        {formData.tiposDocumento.map((tipo) => (
                          <MenuItem key={tipo.id} value={tipo.id}>
                            {tipo.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors.tipo_documento && (
                        <FormHelperText>{fieldErrors.tipo_documento}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Número de Documento"
                      name="numero_documento"
                      value={trabajador.numero_documento}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.numero_documento}
                      helperText={fieldErrors.numero_documento}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Género</InputLabel>
                      <Select
                        name="genero"
                        value={trabajador.genero}
                        onChange={handleChange}
                        label="Género"
                        sx={{ borderRadius: 2 }}
                      >
                        {formData.generos.map((genero) => (
                          <MenuItem key={genero.id} value={genero.id}>
                            {genero.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Fecha de Nacimiento"
                      type="date"
                      name="fecha_nacimiento"
                      value={trabajador.fecha_nacimiento}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Información de Contacto */}
          <Grid item xs={12}>
            <Paper sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(26, 35, 126, 0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
                color: 'white',
                p: { xs: 3, sm: 4 },
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <ContactIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
                  Información de Contacto
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Dirección"
                      name="direccion_trabajador"
                      value={trabajador.direccion_trabajador}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      name="telefono_trabajador"
                      value={trabajador.telefono_trabajador}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.telefono_trabajador}
                      helperText={fieldErrors.telefono_trabajador}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      name="email_trabajador"
                      value={trabajador.email_trabajador}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.email_trabajador}
                      helperText={fieldErrors.email_trabajador}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contacto de Emergencia"
                      name="contacto_emergencia"
                      value={trabajador.contacto_emergencia}
                      onChange={handleChange}
                      placeholder="Nombre y teléfono de contacto de emergencia"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Información Laboral */}
          <Grid item xs={12}>
            <Paper sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(26, 35, 126, 0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                color: 'white',
                p: { xs: 3, sm: 4 },
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <WorkIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
                  Información Laboral
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Profesión"
                      name="profesion"
                      value={trabajador.profesion}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SchoolIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Fecha de Contratación"
                      type="date"
                      name="fecha_contratacion"
                      value={trabajador.fecha_contratacion}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.fecha_contratacion}
                      helperText={fieldErrors.fecha_contratacion}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon sx={{ color: '#4FC3F7' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4FC3F7' },
                          '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required error={!!fieldErrors.id_escala}>
                      <InputLabel>Escala</InputLabel>
                      <Select
                        name="id_escala"
                        value={trabajador.id_escala}
                        onChange={handleChange}
                        label="Escala"
                        sx={{ borderRadius: 2 }}
                      >
                        {formData.escalas.map((escala) => (
                          <MenuItem key={escala.id_escala} value={escala.id_escala}>
                            {escala.descripcion_escala}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors.id_escala && (
                        <FormHelperText>{fieldErrors.id_escala}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required error={!!fieldErrors.id_nivel_usuario}>
                      <InputLabel>Nivel de Usuario</InputLabel>
                      <Select
                        name="id_nivel_usuario"
                        value={trabajador.id_nivel_usuario}
                        onChange={handleChange}
                        label="Nivel de Usuario"
                        sx={{ borderRadius: 2 }}
                      >
                        {formData.nivelesUsuario
                          .filter(nivel => nivel.id_nivel_usuario > user.nivel)
                          .map((nivel) => (
                            <MenuItem key={nivel.id_nivel_usuario} value={nivel.id_nivel_usuario}>
                              {nivel.det_nivel_usuario}
                            </MenuItem>
                          ))}
                      </Select>
                      {fieldErrors.id_nivel_usuario && (
                        <FormHelperText>{fieldErrors.id_nivel_usuario}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Asignaciones */}
          <Grid item xs={12}>
            <Paper sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(26, 35, 126, 0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                color: 'white',
                p: { xs: 3, sm: 4 },
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <AssignmentIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
                  Asignaciones de Locales
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <FormControl fullWidth>
                  <InputLabel>Locales Asignados</InputLabel>
                  <Select
                    multiple
                    value={trabajador.locales}
                    onChange={handleLocalesChange}
                    input={<OutlinedInput label="Locales Asignados" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const local = formData.locales.find(l => l.id_local === value);
                          return (
                            <Chip 
                              key={value} 
                              label={local?.nombre_local || value}
                              size="small"
                              sx={{ 
                                bgcolor: 'rgba(156, 39, 176, 0.1)',
                                color: '#9C27B0'
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    sx={{ borderRadius: 2 }}
                  >
                    {formData.locales.map((local) => (
                      <MenuItem key={local.id_local} value={local.id_local}>
                        <Checkbox checked={trabajador.locales.indexOf(local.id_local) > -1} />
                        <ListItemText 
                          primary={local.nombre_local}
                          secondary={local.direccion_local}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Selecciona los locales donde este trabajador puede ser asignado
                  </FormHelperText>
                </FormControl>
              </Box>
            </Paper>
          </Grid>

          {/* Botones de acción */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, 
              justifyContent: 'flex-end',
              mt: 3
            }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                sx={{
                  borderColor: '#1A237E',
                  color: '#1A237E',
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: '#0D47A1',
                    backgroundColor: 'rgba(26, 35, 126, 0.04)'
                  }
                }}
              >
                Cancelar
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={submitLoading}
                sx={{
                  background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                    boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: '#E0E0E0',
                    color: '#999'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {submitLoading ? 'Guardando...' : (isEditing ? 'Actualizar Trabajador' : 'Crear Trabajador')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Tips y recomendaciones */}
      <Paper sx={{ 
        mt: 3,
        p: 3,
        borderRadius: 3,
        border: '1px solid rgba(33, 150, 243, 0.3)',
        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)'
      }}>
        <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold', mb: 2 }}>
          💡 Consejos para registrar trabajadores
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>• Datos completos:</strong> Asegúrate de llenar toda la información de contacto
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>• Nivel apropiado:</strong> Asigna el nivel de usuario según las responsabilidades
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>• Locales relevantes:</strong> Asigna solo los locales donde trabajará
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>• Contacto emergencia:</strong> Incluye un contacto confiable para emergencias
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default TrabajadorForm;